import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaBars,
  FaBell,
  FaCheck,
  FaCopy,
  FaSignOutAlt,
  FaTh,
  FaUser,
  FaWhatsapp,
} from "react-icons/fa";
import { fetchAllnotification, fetchAllProjects } from "../api/customer.api";
import { DEFAULT_PROJECT_ID, businessPath } from "../config/businessMenu";
import { ASSETS } from "../config/assets";
import { useTheme } from "../theme/ThemeContext";
import { clearSession, getUserName, isSuperAdminUser } from "../utils/auth";
import { mapApiProject, mapProjectsByUser } from "../utils/projectDisplay";
import { allowDevMockFallback } from "../utils/devMockFallback";
import { countUnreadNotifications, markNotificationsSeen } from "../utils/notifications";
import ConnectWhatsapp from "./ConnectWhatsapp";
import ThemeSwitch from "./ThemeSwitch";

const MOCK_PROJECTS = [
  mapApiProject({
    _id: DEFAULT_PROJECT_ID,
    projectname: "",
    contact: "",
    createdAt: "",
    planName: "",
    bluetickverified: false,
  }),
];

const formatNotifiedAt = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

function ProjectCard({ project, onView }) {
  const [copied, setCopied] = useState(false);
  const phone = project.apiNumber || "—";

  const copyPhone = async () => {
    if (!phone || phone === "—") return;
    try {
      await navigator.clipboard.writeText(String(phone));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <article
      className={`all-projects-card${project.waConnected ? " is-connected" : ""}`}
    >
      <header className="all-projects-card-head">
        <div className="all-projects-card-mark" aria-hidden>
          <FaWhatsapp />
        </div>
        <div className="all-projects-card-title">
          <h2>{project.name}</h2>
          <p className="all-projects-card-phone">
            <span>{phone}</span>
            <button
              type="button"
              onClick={copyPhone}
              aria-label="Copy phone"
              disabled={phone === "—"}
            >
              <FaCopy />
            </button>
            {copied && <em className="all-projects-copied">Copied</em>}
          </p>
        </div>
        <span
          className={`all-projects-wa-chip${project.waConnected ? " is-on" : ""}`}
        >
          <span className="all-projects-wa-dot" aria-hidden />
          {project.waConnected ? "Connected" : "Not connected"}
        </span>
      </header>

      <dl className="all-projects-card-meta">
        <div>
          <dt>Active plan</dt>
          <dd>{project.activePlan}</dd>
        </div>
        <div>
          <dt>Created</dt>
          <dd>{project.createdAt}</dd>
        </div>
      </dl>

      <footer className="all-projects-card-foot">
        <span className="all-projects-bluetick">
          <span
            className={`all-projects-bluetick-icon${
              project.bluetickVerified ? "" : " is-empty"
            }`}
            aria-hidden
          >
            {project.bluetickVerified ? <FaCheck /> : null}
          </span>
          {project.bluetickVerified ? "Verified" : "Not verified"}
        </span>
        <button
          type="button"
          className="all-projects-view-btn"
          onClick={() => onView(project.id)}
        >
          View project
        </button>
      </footer>
    </article>
  );
}

const AllProjects = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const notifyRef = useRef(null);
  const [projects, setProjects] = useState(() =>
    allowDevMockFallback() ? MOCK_PROJECTS : [],
  );
  const [projectsByUser, setProjectsByUser] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifyLoading, setNotifyLoading] = useState(true);

  const userName = getUserName() || "User";
  const isSuperAdmin = isSuperAdminUser();
  const connectedCount = projects.filter((project) => project.waConnected).length;

  const groupedSections = useMemo(() => {
    if (!isSuperAdmin) return null;
    if (projectsByUser.length > 0) return projectsByUser;

    const byOwner = new Map();
    projects.forEach((project) => {
      const owner = project.owner;
      const key = owner?.id || owner?.email || "unassigned";
      if (!byOwner.has(key)) {
        byOwner.set(key, {
          user: owner || {
            id: "unassigned",
            name: "Unassigned",
            email: "",
            type: "",
          },
          projects: [],
        });
      }
      byOwner.get(key).projects.push(project);
    });
    return [...byOwner.values()];
  }, [isSuperAdmin, projects, projectsByUser]);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const res = await fetchAllProjects();
      const list = (res?.projects || []).map(mapApiProject);
      setProjects(list);
      setProjectsByUser(mapProjectsByUser(res?.projectsByUser || []));
    } catch (err) {
      if (allowDevMockFallback()) {
        setProjects(MOCK_PROJECTS);
        setProjectsByUser([]);
        setError(err.message || "Using sample projects");
      } else {
        setProjects([]);
        setProjectsByUser([]);
        setError(err.message || "Failed to load projects");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchAllnotification();
        if (cancelled) return;
        const list = Array.isArray(res) ? res : res?.data || [];
        const next = Array.isArray(list) ? list : [];
        setNotifications(next);
        setUnreadCount(countUnreadNotifications(next));
      } catch {
        if (!cancelled) setNotifications([]);
      } finally {
        if (!cancelled) setNotifyLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!notifyOpen) return undefined;
    const onPointerDown = (event) => {
      if (notifyRef.current && !notifyRef.current.contains(event.target)) {
        setNotifyOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setNotifyOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [notifyOpen]);

  useEffect(() => {
    if (!notifyOpen || notifications.length === 0) return;
    markNotificationsSeen(notifications);
    setUnreadCount(0);
  }, [notifyOpen, notifications]);

  const handleLogout = () => {
    clearSession();
    navigate("/login");
  };

  const toggleNotifications = () => {
    setNotifyOpen((open) => !open);
  };

  return (
    <div className={`biz-app${mobileNavOpen ? " biz-nav-open" : ""}`}>
      <button
        type="button"
        className="biz-sidebar-overlay"
        aria-label="Close navigation menu"
        onClick={() => setMobileNavOpen(false)}
      />

      <aside
        className={`biz-sidebar ${collapsed ? "biz-sidebar-collapsed" : ""}`}
        style={{ backgroundColor: theme.primary }}
      >
        <div className="biz-sidebar-brand">
          <div className="biz-sidebar-brand-lockup">
            {!collapsed && (
              <img
                src={ASSETS.brandLogo}
                alt={ASSETS.brandName}
                className="biz-sidebar-logo"
              />
            )}
          </div>
          <button
            type="button"
            className="biz-sidebar-toggle"
            onClick={() => setCollapsed((value) => !value)}
            aria-label="Toggle sidebar"
          >
            <FaBars />
          </button>
        </div>
        <nav className="biz-sidebar-nav">
          <span className="biz-nav-item biz-nav-item-active" title="Projects">
            <FaTh className="biz-nav-icon" />
            {!collapsed && <span>Projects</span>}
          </span>
          <button
            type="button"
            className="biz-nav-item biz-nav-logout"
            onClick={handleLogout}
            title={collapsed ? "Logout" : undefined}
          >
            <FaSignOutAlt className="biz-nav-icon" />
            {!collapsed && <span>Logout</span>}
          </button>
        </nav>
      </aside>

      <div className={`biz-main${collapsed ? " biz-main-collapsed" : ""}`}>
        <header className="biz-topbar">
          <div className="biz-topbar-left">
            <button
              type="button"
              className="biz-menu-btn u-hide-desktop"
              onClick={() => setMobileNavOpen((open) => !open)}
              aria-label="Open navigation menu"
            >
              <FaBars />
            </button>
            <span className="biz-all-projects-btn" aria-current="page">
              <FaTh />
              <span>Projects</span>
            </span>
          </div>

          <div className="biz-topbar-right">
            <ThemeSwitch />
            <span className="biz-topbar-divider" aria-hidden />
            <div className="biz-notify-wrap" ref={notifyRef}>
              <button
                type="button"
                className="biz-topbar-link biz-notify-btn"
                onClick={toggleNotifications}
                aria-haspopup="true"
                aria-expanded={notifyOpen}
                aria-label={`Notifications (${unreadCount})`}
              >
                <FaBell />
                {unreadCount > 0 && (
                  <span className="biz-notify-badge">{unreadCount}</span>
                )}
              </button>

              {notifyOpen && (
                <div className="biz-notify-panel" role="dialog" aria-label="Notifications">
                  <div className="biz-notify-panel-head">
                    <strong>Notifications</strong>
                    <button
                      type="button"
                      className="biz-notify-panel-close"
                      onClick={() => setNotifyOpen(false)}
                      aria-label="Close notifications"
                    >
                      ×
                    </button>
                  </div>
                  <div className="biz-notify-panel-body">
                    {notifyLoading ? (
                      <p className="biz-notify-empty">Loading…</p>
                    ) : notifications.length === 0 ? (
                      <p className="biz-notify-empty">No notifications yet.</p>
                    ) : (
                      <ul className="biz-notify-list">
                        {notifications.map((item, index) => (
                          <li key={item._id || index} className="biz-notify-item">
                            <p className="biz-notify-item-title">
                              {item.title || "Notification"}
                            </p>
                            {item.description && (
                              <p className="biz-notify-item-text">{item.description}</p>
                            )}
                            <span className="biz-notify-item-time">
                              {formatNotifiedAt(item.createdAt)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="biz-content">
          <div className="biz-dashboard all-projects-page">
            <div className="all-projects-heading">
              <div>
                <h1>WABA Projects</h1>
                <p>
                  Welcome back, {userName}.{" "}
                  {isSuperAdmin
                    ? "You can view every admin’s projects."
                    : "Select a project to open the dashboard."}
                </p>
              </div>
              <div className="all-projects-heading-actions">
                {!loading && projects.length > 0 && (
                  <div className="all-projects-summary" aria-live="polite">
                    <span>
                      {projects.length}{" "}
                      {projects.length === 1 ? "project" : "projects"}
                    </span>
                    <span>
                      {connectedCount} connected
                    </span>
                  </div>
                )}
                <ConnectWhatsapp
                  projectId={null}
                  trigger={({ onClick }) => (
                    <button
                      type="button"
                      className="all-projects-create-btn"
                      onClick={onClick}
                    >
                      <FaWhatsapp aria-hidden />
                      Connect with WhatsApp
                    </button>
                  )}
                  onConnected={loadProjects}
                />
              </div>
            </div>

            {error && <p className="all-projects-error">{error}</p>}

            {loading ? (
              <div className="all-projects-grid" aria-busy="true">
                {[0, 1, 2].map((key) => (
                  <div key={key} className="all-projects-skel" />
                ))}
              </div>
            ) : projects.length === 0 ? (
              <article className="all-projects-empty">
                <div className="all-projects-empty-mark" aria-hidden>
                  <FaWhatsapp />
                </div>
                <h3>No projects yet</h3>
                <p>Connect WhatsApp to create your first WABA project.</p>
              </article>
            ) : isSuperAdmin && groupedSections?.length ? (
              <div className="all-projects-by-user">
                {groupedSections.map((section) => (
                  <section
                    key={section.user.id || section.user.email}
                    className="all-projects-user-section"
                  >
                    <header className="all-projects-user-head">
                      <div className="all-projects-user-mark" aria-hidden>
                        <FaUser />
                      </div>
                      <div>
                        <h2>{section.user.name}</h2>
                        <p>
                          {section.user.email || "No email"}
                          {section.user.type
                            ? ` · ${String(section.user.type)}`
                            : ""}
                          {` · ${section.projects.length} project${
                            section.projects.length === 1 ? "" : "s"
                          }`}
                        </p>
                      </div>
                    </header>
                    <div className="all-projects-grid">
                      {section.projects.map((project) => (
                        <ProjectCard
                          key={project.id}
                          project={project}
                          onView={(projectId) =>
                            navigate(businessPath(projectId, "dashboard"))
                          }
                        />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            ) : (
              <div className="all-projects-grid">
                {projects.map((project) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    onView={(projectId) =>
                      navigate(businessPath(projectId, "dashboard"))
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllProjects;
